/**
 * What the platform knows about the plant (plant-analytics B-6, brief 4.7): typed wrappers over the knowledge
 * store's read routes and the plant questions' routes, plus their hooks. Backend `internal/knowledge` (api.go,
 * read.go, questions_api.go) and `internal/twin/plant_question_actions.go` (apply).
 *
 *   GET  /v1/twin/knowledge/findings?limit=                 the org's findings, as the caller may see them
 *   GET  /v1/twin/knowledge/findings/{finding_id}           one, with its live evidence and its events
 *   POST /v1/twin/knowledge/findings/{finding_id}/override  knowledge.respond: {action, reason} (B-6b)
 *   GET  /v1/twin/knowledge/questions?limit=                the plant questions (drafts: platform admins only)
 *   POST /v1/twin/knowledge/questions/{question_id}/answer  knowledge.respond: {text, verdict?, actions?}
 *   POST /v1/twin/knowledge/questions/{question_id}/apply   org.admin: {action_index}, one suggested action
 *   POST /v1/twin/knowledge/questions/bulk/approve          platform admins: {ids}, drafts approved together (B4)
 *   POST /v1/twin/knowledge/questions/bulk/ask              platform admins: {ids, via}, approved ones asked together
 *   PUT  /v1/twin/knowledge/questions/{question_id}/assignment  knowledge.respond: {owner, due} (B4 T3)
 *   POST /v1/twin/knowledge/questions/raise                 knowledge.respond: a plant member's own question to Sahas
 *
 * WHO SEES WHAT is the server's (read.go), and every response says which view the caller got. "hidden" means
 * the org's customer switch (org_settings.knowledge_customer_visible, off by default) is off for a caller who
 * is not a platform admin: the page reads that as its gate (knowledgeGateOf), and fails closed.
 *
 * A person acknowledges a finding (the plant agrees the pattern is real) or disputes it (the plant says it is
 * not) through the override route (B-6b), with a required reason; the plant also confirms or contradicts a
 * finding through its answer to a question about it. The nav reads the same view (useKnowledgeView) to drop
 * the Knowledge item's "Coming soon" pill for callers who can see the page.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { useAuth } from "@/lib/auth";
import { useOrg } from "@/lib/org-context";
import { ANALYSIS_JOBS_KEY } from "./analysis-jobs";
import { statusOf } from "./error";

export type FindingStatus = "candidate" | "confirmed" | "acknowledged" | "retired" | "contradicted" | "superseded";
export type KnowledgeView = "internal" | "customer" | "hidden";

/** What a finding is about, as ids (never names). */
export interface FindingSubject {
  tags?: string[];
  entities?: string[];
  products?: string[];
  defects?: string[];
  connection?: string;
  stream?: string;
  strata?: string[];
}

/** One finding on the wire (read.go FindingView). The fields marked internal are absent from the customer view. */
export interface KnowledgeFinding {
  id: string;
  fingerprint?: string; // internal
  class: "property" | "event";
  kind: string;
  subject: FindingSubject | null;
  grain: string;
  claim_type: "fact" | "inference";
  direction: "pos" | "neg" | "up" | "down" | "none";
  /**
   * What it claims, by key (numbers stay in the evidence): a template and its keys. `derived` (2026-10-28) is how an
   * input the analysis worked out from measured values was worked out, in words; absent for a measured input.
   */
  statement: Record<string, unknown> | null;
  /** The numbers it was frozen with when found: an operating band's band_lo and band_hi, among others. */
  frozen?: { band_lo?: number; band_hi?: number; method?: string; [k: string]: unknown } | null;
  status: FindingStatus;
  derived_status?: string; // internal
  event_state: string | null;
  confirmation_plan: unknown;
  confidence: "low" | "medium" | "high";
  confidence_basis: string[];
  source: "engine" | "brain" | "human";
  attributed_to?: string; // internal
  review_state: string;
  visibility: "internal" | "customer";
  mechanism_id: string | null;
  reference_ids: string[];
  discovery_evidence_id: string | null;
  current_evidence_id: string | null;
  first_seen_at: string;
  first_seen_from: string | null;
  first_seen_to: string | null;
  last_confirmed_at: string | null;
  last_confirmed_from: string | null;
  last_confirmed_to: string | null;
  windows_adequate: number;
  windows_replicated: number;
  windows_contradicting: number;
  superseded_by: string | null;
  question_id: string | null;
  created_at: string;
  updated_at: string;
}

/** K5 "effect": r, rho, a rate difference, a lag. */
export interface EvidenceEffect {
  kind: string;
  value: number;
  unit?: string;
}

/** K5 "data_refs": what data fed a record. */
export interface EvidenceDataRefs {
  import_run_ids?: string[];
  stream_days_hash?: string;
  query_hash?: string;
  backfilled?: boolean;
}

/** K5 "input_dq": the data quality of a record's inputs, with the caveats that cap confidence. */
export interface EvidenceInputDQ {
  session_coverage?: number;
  stuck_masked?: number;
  idle_removed?: number;
  censored_share?: number;
  co_logged_group?: string;
  caveats?: string[];
}

/** One live evidence row (read.go EvidenceView): one window, one method. */
export interface KnowledgeEvidence {
  id: string;
  role: "discovery" | "replication" | "evaluation" | "figure";
  method: string;
  method_version: string;
  computed_by: string;
  grain: string;
  /** Plant-time dates, half-open [from, to). */
  window: { from: string; to: string };
  outcome: "significant" | "not_significant" | "inconclusive" | "precondition_failed" | "descriptive";
  n: number | null;
  n_eff: number | null;
  effect: EvidenceEffect | null;
  /**
   * A method's own numbers: a data-quality check's rows, count, share and value; an operating band's p5, p95, mean and
   * stdev. Shown in words by findingMeaning.
   */
  values?: Record<string, unknown> | null;
  ci: { lo: number; hi: number; level: number | null } | null;
  p: number | null;
  q: number | null;
  /** The BH family q was computed in, and its size m. */
  family: { id: string; m: number | null; run_id: string | null } | null;
  data_refs: EvidenceDataRefs | null;
  input_dq: EvidenceInputDQ | null;
  preconditions: { check: string; result: string }[] | null;
  verification: string;
  analysis_run_id: string | null;
  computed_at: string;
}

/** One event of a finding's timeline (read.go EventView). */
export interface KnowledgeEvent {
  id: string;
  at: string;
  kind: string;
  from_status: string | null;
  to_status: string | null;
  evidence_id: string | null;
  actor_kind: "engine" | "brain" | "user" | "plant";
  actor_id: string;
  reason: string;
  internal: boolean;
}

export interface FindingPage {
  items: KnowledgeFinding[];
  total: number;
  limit: number;
  offset: number;
  view: KnowledgeView;
}

export interface FindingDetail {
  finding: KnowledgeFinding;
  evidence: KnowledgeEvidence[];
  events: KnowledgeEvent[];
  view: KnowledgeView;
}

/** raised: a plant member's own question to Sahas (B4 T3), waiting for Sahas's answer. */
export type QuestionStatus = "draft" | "approved" | "asked" | "answered" | "closed" | "withdrawn" | "raised";
/** Who drafted a question (plant_questions.source); plant: a plant member raised it. The wire may carry one this page
 *  does not know yet. */
export type QuestionSource = "study" | "brain_author" | "engine" | "human" | "data_ask" | "plant";
/** The channels a platform admin asks through from the page (the server also takes copilot:<thread id>). */
export type AskVia = "app" | "email" | "phone";
export type QuestionPriority = "blocks_import" | "blocks_analysis" | "good_to_know";
export type ActionType = "set_plant_time" | "set_lag" | "rename_entity";
export type AnswerVerdict = "confirms" | "contradicts";

/** A person's decision on a finding: the plant agrees the pattern is real, or says it is not. */
export type FindingDecision = "acknowledge" | "dispute";

/** POST .../findings/{finding_id}/override's body; the reason is required (1 to 1000 characters). */
export interface OverrideBody {
  action: FindingDecision;
  reason: string;
}

/** The override route's answer: the finding as the caller now sees it, and whether the call changed anything. */
export interface OverrideResult extends FindingDetail {
  changed: boolean;
}

/**
 * A suggested action (questions.go Action): only its type's fields are set. A set_plant_time sets the clock (tz and
 * day_start, together), and/or the shift calendar, and/or the week start; what it leaves out stays as it is.
 */
export interface SuggestedAction {
  type: ActionType;
  tz?: string;
  day_start?: string;
  shift_calendar?: { start: string; label: string }[];
  week_start?: number;
  connection_id?: string;
  min_s?: number;
  max_s?: number;
  /** set_lag: a count lag (kind count) states its counts and may state its cycle times; method says how it was found. */
  kind?: "time" | "count";
  count_min?: number;
  count_max?: number;
  cycle_s_min?: number;
  cycle_s_max?: number;
  method?: string;
  entity_id?: string;
  name?: string;
}

/**
 * The shape a question's answer takes (B4, migration 0251): free text (the default), a value with a unit (none: the
 * plant names it), one of two to twenty options, a date, or a reference to a file. The answer form follows it.
 */
export interface AnswerShape {
  kind: "text" | "value" | "choice" | "date" | "file";
  unit?: string;
  options?: string[];
}

/** An answer in its question's shape, stored beside its text: only the shape's fields are set. */
export interface AnswerValue {
  kind: "value" | "choice" | "date" | "file";
  number?: number;
  unit?: string;
  choice?: string;
  /** YYYY-MM-DD. */
  date?: string;
  file?: string;
}

export interface PlantAnswer {
  text: string;
  verdict?: AnswerVerdict;
  actions: SuggestedAction[];
}

/** One applied action: which, when, by whom, and what it changed. */
export interface AppliedAction {
  index: number;
  action: SuggestedAction;
  applied_at: string;
  applied_by: string | null;
  before: unknown;
  after: unknown;
}

/** One plant question on the wire (questions_store.go QuestionView). */
export interface PlantQuestion {
  id: string;
  key?: string; // internal
  parent_id: string | null;
  topic: string;
  what_we_saw: Record<string, unknown> | null;
  what_we_assume: string;
  what_it_changes: string;
  priority: QuestionPriority;
  data_answer_finding_id: string | null;
  source: string;
  status: QuestionStatus;
  asked_at: string | null;
  asked_via: string | null;
  answer: PlantAnswer | null;
  answered_by: string | null;
  answered_on_behalf_of: string | null;
  answered_at: string | null;
  resulting_finding_id: string | null;
  applied: AppliedAction[] | null;
  created_at: string;
  /** The shape its answer takes; absent from an older API, which reads as free text. */
  answer_shape?: AnswerShape;
  /** The answer in its shape, beside answer.text; null for free text and until answered. */
  answer_value?: AnswerValue | null;
  /**
   * The org member who owns it and the plant day it is due (B4 T3), as an action's owner and due date are; who set
   * them last. Overdue once the due day has passed while the question is still open. Absent from an older API.
   */
  owner?: string | null;
  owner_name?: string;
  due?: string | null;
  assigned_by?: string | null;
  assigned_at?: string | null;
  /** The plant member who raised it (source plant). */
  raised_by?: string | null;
  raised_by_name?: string;
}

/** A member a question may be given to: the owner picker's entries. */
export interface QuestionPerson {
  user_id: string;
  name: string;
}

/** PUT .../assignment's body: a member's users.id ("" for none) and a plant day ("" for none). Both empty clears. */
export interface QuestionAssignment {
  owner: string;
  due: string;
}

/** POST .../questions/raise's body: a plant member's own question to Sahas. */
export interface RaiseBody {
  topic: string;
  what_we_see?: string;
  why_it_matters?: string;
  priority?: QuestionPriority;
  owner?: string;
  due?: string;
}

export interface QuestionPage {
  items: PlantQuestion[];
  total: number;
  limit: number;
  offset: number;
  view: KnowledgeView;
  /** The owner picker's members, for a caller who may set an owner (B4 T3); absent from an older API. */
  people?: QuestionPerson[];
}

export interface QuestionDetail {
  question: PlantQuestion;
  sub_questions: PlantQuestion[];
  view: KnowledgeView;
}

/** A bulk call's answer: the questions it named as they now stand, and how many it moved (sub-questions included). */
export interface BulkQuestions {
  items: PlantQuestion[];
  moved: number;
  view: KnowledgeView;
}

/** A bulk move a platform admin makes from the page: approve drafts, or ask approved questions through a channel. */
export type BulkMove = { move: "approve"; ids: string[] } | { move: "ask"; ids: string[]; via: AskVia };

export interface AnswerBody {
  /** The plant's words: required for free text; beside a value, anything more (the value in words when empty). */
  text: string;
  verdict?: AnswerVerdict;
  actions?: SuggestedAction[];
  /** The answer in the question's shape: required for any shape but free text. */
  value?: AnswerValue;
}

/** The page reads at most this many findings and questions at once (the server's cap). */
export const KNOWLEDGE_PAGE_LIMIT = 200;

const q = (id: string) => `/v1/twin/knowledge/questions/${encodeURIComponent(id)}`;
const f = (id: string) => `/v1/twin/knowledge/findings/${encodeURIComponent(id)}`;

export const knowledgeApi = {
  findings: () => api.get<FindingPage>(`/v1/twin/knowledge/findings?limit=${KNOWLEDGE_PAGE_LIMIT}`),
  /** The findings list's view alone: one finding asked for, for the nav. */
  view: () => api.get<FindingPage>("/v1/twin/knowledge/findings?limit=1"),
  finding: (id: string) => api.get<FindingDetail>(f(id)),
  override: (id: string, body: OverrideBody) => api.post<OverrideResult>(`${f(id)}/override`, body),
  questions: () => api.get<QuestionPage>(`/v1/twin/knowledge/questions?limit=${KNOWLEDGE_PAGE_LIMIT}`),
  /** One page of the questions from offset, for the export that reads them all (B4 T4). */
  questionsAt: (offset: number) =>
    api.get<QuestionPage>(`/v1/twin/knowledge/questions?limit=${KNOWLEDGE_PAGE_LIMIT}&offset=${Math.max(0, Math.floor(offset))}`),
  answer: (id: string, body: AnswerBody) =>
    api.post<{ question: QuestionDetail; plant_fact_id: string }>(`${q(id)}/answer`, body),
  /** Sahas's reply to a question the plant raised (B4 T3; a platform admin): no plant fact. */
  reply: (id: string, body: AnswerBody) => api.post<{ question: QuestionDetail }>(`${q(id)}/reply`, body),
  assign: (id: string, body: QuestionAssignment) => api.put<QuestionDetail>(`${q(id)}/assignment`, body),
  raise: (body: RaiseBody) => api.post<QuestionDetail>("/v1/twin/knowledge/questions/raise", body),
  bulkApprove: (ids: string[]) => api.post<BulkQuestions>("/v1/twin/knowledge/questions/bulk/approve", { ids }),
  bulkAsk: (ids: string[], via: AskVia) => api.post<BulkQuestions>("/v1/twin/knowledge/questions/bulk/ask", { ids, via }),
  apply: (id: string, actionIndex: number) =>
    api.post<{ question: QuestionDetail; applied: AppliedAction; refresh_reason: string }>(`${q(id)}/apply`, {
      action_index: actionIndex,
    }),
};

/** What the page may show: nothing yet, nothing at all, the customer view, everything, or a failure. */
export type KnowledgeGate = "loading" | "hidden" | "customer" | "internal" | "error";

/**
 * The page's gate, pure. It reads the view the server gave the findings list, so the web never re-derives
 * read.go's rule. FAIL CLOSED: no live API (stub mode, no org, signed out), a view it does not know, and a
 * refusal (403: the vendor overlay or no data.read) all read "hidden"; any other failure is "error", which
 * shows no knowledge either.
 */
export function knowledgeGateOf(live: boolean, page: Pick<FindingPage, "view"> | undefined, error: unknown): KnowledgeGate {
  if (!live) return "hidden";
  if (page) return page.view === "internal" || page.view === "customer" ? page.view : "hidden";
  if (error) return statusOf(error) === 403 ? "hidden" : "error";
  return "loading";
}

const isLiveApiFlag = import.meta.env.VITE_USE_LIVE_API === "true";

/** True when the knowledge routes can be asked at all: a live API, a signed-in user, an active org. */
export function useKnowledgeLive(): boolean {
  const { isAuthenticated } = useAuth();
  const { activeOrgId } = useOrg();
  return isLiveApiFlag && isAuthenticated && activeOrgId !== null;
}

export const KNOWLEDGE_FINDINGS_KEY = ["twin-knowledge-findings"] as const;
export const KNOWLEDGE_FINDING_KEY = ["twin-knowledge-finding"] as const;
export const KNOWLEDGE_QUESTIONS_KEY = ["twin-knowledge-questions"] as const;
/** The staff line's counts of what the plant sees (PlantSeesLine; GET /v1/admin/orgs/{org_id}/knowledge-visible), per org. */
export const KNOWLEDGE_CUSTOMER_VIEW_KEY = ["knowledge-customer-view"] as const;
export const KNOWLEDGE_VIEW_KEY = ["twin-knowledge-view"] as const;

/** The org's findings as the caller may see them; its `view` is the page's gate. A 403 is not retried. */
export function useKnowledgeFindings() {
  return useQuery({
    queryKey: KNOWLEDGE_FINDINGS_KEY,
    queryFn: knowledgeApi.findings,
    enabled: useKnowledgeLive(),
    staleTime: 60_000,
    retry: (count, err) => statusOf(err) !== 403 && count < 2,
  });
}

/** One finding with its evidence and events, for the drawer. */
export function useKnowledgeFinding(id: string | null) {
  const live = useKnowledgeLive();
  return useQuery({
    queryKey: [...KNOWLEDGE_FINDING_KEY, id ?? ""],
    queryFn: () => knowledgeApi.finding(id as string),
    enabled: live && !!id,
    staleTime: 60_000,
  });
}

/** The plant questions. `enabled` is the page's gate: nothing is asked for a page that stays hidden. */
export function useKnowledgeQuestions(enabled: boolean) {
  const live = useKnowledgeLive();
  return useQuery({
    queryKey: KNOWLEDGE_QUESTIONS_KEY,
    queryFn: knowledgeApi.questions,
    enabled: live && enabled,
    staleTime: 60_000,
  });
}

/** Record the plant's answer. It creates a plant fact and may move the finding it is about, so both reload. */
export function useAnswerQuestion() {
  const qc = useQueryClient();
  return useMutation({
    // A question the plant raised takes Sahas's reply (its own route); any other, the plant's answer.
    mutationFn: ({ id, body, raised }: { id: string; body: AnswerBody; raised?: boolean }) =>
      raised ? knowledgeApi.reply(id, body) : knowledgeApi.answer(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KNOWLEDGE_QUESTIONS_KEY });
      void qc.invalidateQueries({ queryKey: KNOWLEDGE_FINDINGS_KEY });
      void qc.invalidateQueries({ queryKey: KNOWLEDGE_CUSTOMER_VIEW_KEY }); // the staff line's counts (PlantSeesLine)
      void qc.invalidateQueries({ queryKey: KNOWLEDGE_FINDING_KEY });
    },
  });
}

/** Set a question's owner and due date (knowledge.respond, or a platform admin). The list reloads. */
export function useAssignQuestion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: QuestionAssignment }) => knowledgeApi.assign(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KNOWLEDGE_QUESTIONS_KEY });
      void qc.invalidateQueries({ queryKey: KNOWLEDGE_CUSTOMER_VIEW_KEY }); // the staff line's counts (PlantSeesLine)
    },
  });
}

/** Raise a plant member's own question to Sahas. The list reloads. */
export function useRaiseQuestion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RaiseBody) => knowledgeApi.raise(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KNOWLEDGE_QUESTIONS_KEY });
      void qc.invalidateQueries({ queryKey: KNOWLEDGE_CUSTOMER_VIEW_KEY }); // the staff line's counts (PlantSeesLine)
    },
  });
}

/** Approve or ask many questions at once (platform admins): all of them move, or none does. The list reloads. */
export function useBulkMoveQuestions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (m: BulkMove) => (m.move === "approve" ? knowledgeApi.bulkApprove(m.ids) : knowledgeApi.bulkAsk(m.ids, m.via)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KNOWLEDGE_QUESTIONS_KEY });
      void qc.invalidateQueries({ queryKey: KNOWLEDGE_CUSTOMER_VIEW_KEY }); // the staff line's counts (PlantSeesLine)
    },
  });
}

/** Apply one suggested action to the settings (org admins). The server queues an analysis refresh with it. */
export function useApplyQuestionAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, index }: { id: string; index: number }) => knowledgeApi.apply(id, index),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KNOWLEDGE_QUESTIONS_KEY });
      void qc.invalidateQueries({ queryKey: KNOWLEDGE_CUSTOMER_VIEW_KEY }); // the staff line's counts (PlantSeesLine)
      void qc.invalidateQueries({ queryKey: ANALYSIS_JOBS_KEY });
      // A set_plant_time changed the plant time every page reads (one cached read, ["plant-time"]).
      void qc.invalidateQueries({ queryKey: ["plant-time"] });
    },
  });
}

/**
 * Record a person's acknowledgement or dispute of a finding. The answer is the finding as the caller now sees
 * it, so it goes straight into the drawer's cache (a dispute takes a finding out of the customer view, where
 * asking for it again would be 404); the list reloads, and a finding that left the view leaves the list.
 */
export function useDecideFinding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: OverrideBody }) => knowledgeApi.override(id, body),
    onSuccess: (res, { id }) => {
      qc.setQueryData([...KNOWLEDGE_FINDING_KEY, id], res);
      void qc.invalidateQueries({ queryKey: KNOWLEDGE_FINDINGS_KEY });
      void qc.invalidateQueries({ queryKey: KNOWLEDGE_CUSTOMER_VIEW_KEY }); // the staff line's counts (PlantSeesLine)
    },
  });
}

/**
 * The view the findings list answers the caller with, asked for with one finding, for the nav. `enabled` is the
 * nav item's own gate (the twin pack): nothing is asked for an item the nav does not show. A 403 is not retried.
 */
export function useKnowledgeView(enabled: boolean) {
  const live = useKnowledgeLive();
  return useQuery({
    queryKey: KNOWLEDGE_VIEW_KEY,
    queryFn: knowledgeApi.view,
    enabled: live && enabled,
    staleTime: 60_000,
    retry: (count, err) => statusOf(err) !== 403 && count < 2,
  });
}

/**
 * The Knowledge nav item's pill, pure: none for a caller who can see the page (the server's view is customer or
 * internal), "Coming soon" for everyone else. It fails closed like the page's gate: loading, hidden and a
 * failure all keep the pill.
 */
export function knowledgeNavBadge(gate: KnowledgeGate): string | undefined {
  return gate === "customer" || gate === "internal" ? undefined : "Coming soon";
}
